<template>
    <div class="w-full overflow-x-auto">
        <table class="w-full text-sm">
            <thead>
                <tr>
                    <th
                        class="py-2 pr-3 text-left font-medium text-theme-muted"
                    >
                        Incluye
                    </th>
                    <th
                        v-for="plan in plans"
                        :key="plan.slug"
                        class="px-2 py-2 text-center font-semibold"
                        :class="
                            isCurrent(plan)
                                ? 'text-theme-primary'
                                : 'text-theme-base'
                        "
                    >
                        {{ plan.name }}
                        <template v-if="showPrice">
                            <span
                                class="block text-xs font-normal text-theme-muted whitespace-nowrap"
                            >
                                {{ plan.price | currency }}
                            </span>
                            <span
                                class="block text-2xs font-normal text-theme-muted"
                            >
                                {{ periodSuffix(plan) }}
                            </span>
                            <span
                                v-if="plan.period !== 'monthly'"
                                class="block text-2xs font-normal text-theme-muted"
                            >
                                {{ monthlyEquivalent(plan) | currency }}/mes
                            </span>
                            <span
                                v-if="savings(plan) > 0"
                                class="block text-2xs font-bold text-theme-success"
                            >
                                Ahorras {{ savings(plan) }} %
                            </span>
                        </template>
                        <span
                            v-if="isCurrent(plan)"
                            class="block text-xs font-normal"
                        >
                            Tu plan
                        </span>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="row in rows"
                    :key="row.key"
                    class="border-t border-theme-border"
                >
                    <td class="py-2 pr-3 text-theme-base">{{ row.label }}</td>
                    <td
                        v-for="cell in row.cells"
                        :key="cell.slug"
                        class="px-2 py-2 text-center"
                        :class="cellClass(cell)"
                    >
                        {{ cell.text }}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import {
    ENTITLEMENT_FEATURES,
    PLAN_FEATURES,
    PLAN_RESOURCES,
    planIncludesFeature as _hasFeature,
    planLimitOf as _limitOf
} from "@/common/fields/plan-features";
import {
    PLAN_PERIOD_SUFFIXES,
    monthlyEquivalent,
    savingsPercent
} from "@/common/lib/plan-periods";

// null (sin límite) es más que cualquier número.
const _isMore = (limit, base) =>
    limit === null ? base !== null : base !== null && limit > base;

export default {
    name: "PlanComparisonTable",
    props: {
        // Planes de `GET /bo/platform-plans` (con `features` y `entitlements`).
        plans: {
            type: Array,
            default: () => []
        },
        currentPlanSlug: {
            type: String,
            default: null
        },
        showPrice: {
            type: Boolean,
            default: false
        },
        // Fila mensual de cada plan, por `code`: referencia para el ahorro
        // cuando `plans` trae filas trimestrales o anuales.
        monthlyPlans: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        currentPlan() {
            return (
                this.plans.find((plan) => plan.slug === this.currentPlanSlug) ||
                null
            );
        },
        rows() {
            const resourceRows = PLAN_RESOURCES.map((resource) => ({
                key: `resource-${resource.resourceCode}`,
                label:
                    resource.label.charAt(0).toUpperCase() +
                    resource.label.slice(1),
                cells: this.plans.map((plan) => {
                    const limit = _limitOf(plan, resource.resourceCode);
                    const base = this.currentPlan
                        ? _limitOf(this.currentPlan, resource.resourceCode)
                        : 0;
                    return {
                        slug: plan.slug,
                        text: limit === null ? "Sin límite" : String(limit),
                        current: this.isCurrent(plan),
                        gain: !this.isCurrent(plan) && _isMore(limit, base)
                    };
                })
            }));
            const featureRows = PLAN_FEATURES.filter(
                (item) => !ENTITLEMENT_FEATURES.includes(item.feature)
            ).map((item) => ({
                key: `feature-${item.feature}`,
                label: item.label,
                cells: this.plans.map((plan) => {
                    const has = _hasFeature(plan, item.feature);
                    const baseHas = this.currentPlan
                        ? _hasFeature(this.currentPlan, item.feature)
                        : false;
                    return {
                        slug: plan.slug,
                        text: has ? "✓" : "–",
                        current: this.isCurrent(plan),
                        gain: !this.isCurrent(plan) && has && !baseHas
                    };
                })
            }));
            return [...resourceRows, ...featureRows];
        }
    },
    methods: {
        periodSuffix(plan) {
            return PLAN_PERIOD_SUFFIXES[plan.period];
        },
        monthlyEquivalent(plan) {
            return monthlyEquivalent(plan);
        },
        savings(plan) {
            return savingsPercent(plan, this.monthlyPlans[plan.code]);
        },
        isCurrent(plan) {
            return plan.slug === this.currentPlanSlug;
        },
        cellClass(cell) {
            if (cell.gain) return "font-semibold text-theme-success";
            if (cell.current) return "text-theme-primary";
            return "text-theme-muted";
        }
    }
};
</script>
